import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import AuthFrame from './AuthFrame';
import PasswordField from './PasswordField';
import PasswordRequirements from './PasswordRequirements';
import { passwordRequirementChecks } from '../../lib/passwordValidation';
import LoadingSpinner from '../common/LoadingSpinner';

export default function SetPassword() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('accountAccess');
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState<{ email: string; full_name: string } | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const redirectTimer = useRef<ReturnType<typeof setTimeout>>();
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = passwordRequirementChecks(password).every(Boolean) && passwordsMatch && !submitting;

  useEffect(() => {
    let active = true;
    const verify = async () => {
      setLoading(true);
      if (!token) { setError(t('invalidLink')); setLoading(false); return; }
      try {
        const result = await authApi.verifyInvitation(token);
        if (active) { setUserInfo({ email: result.email, full_name: result.full_name }); setError(null); }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : t('invalidExpired'));
      } finally { if (active) setLoading(false); }
    };
    void verify();
    return () => { active = false; };
  }, [token, t]);

  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      await authApi.acceptInvitation(token, password);
      setSuccess(true);
      redirectTimer.current = setTimeout(async () => { await refreshProfile(); navigate('/candidate'); }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failed'));
      setSubmitting(false);
    }
  };

  if (loading) return <AuthFrame><div className="flex flex-col items-center gap-5 py-8"><LoadingSpinner size="lg" /><p>{t('verifying')}</p></div></AuthFrame>;
  if (error && !userInfo) return <AuthFrame><AlertTriangle className="mb-5 text-[var(--color-error)]" size={32} /><h1>{t('invalidTitle')}</h1><p role="alert">{error}</p><Link to="/login" className="btn-primary mt-6 inline-block">{t('signIn')}</Link></AuthFrame>;
  if (success) return <AuthFrame><div role="status"><CheckCircle className="mb-5 text-[var(--color-success)]" size={32} /><h1>{t('success')}</h1><p className="mt-3 text-sm text-[var(--text-secondary)]">{t('welcome', { name: userInfo?.full_name })}</p><p className="mt-3 text-sm">{t('redirecting')}</p></div></AuthFrame>;

  return <AuthFrame>
    <h1>{t('title')}</h1><p>{t('subtitle')}</p>
    <div className="mt-6 rounded-xl border border-[var(--border-light)] bg-[var(--bg-secondary)] p-4"><p className="font-semibold">{userInfo?.full_name}</p><p className="break-all text-sm text-[var(--text-secondary)]">{userInfo?.email}</p></div>
    <form onSubmit={handleSubmit} className="mt-6 space-y-5" aria-busy={submitting}>
      {error && <div id="invitation-error" role="alert" className="theme-banner-danger rounded-xl p-3 text-sm">{error}</div>}
      <PasswordField id="invitation-password" label={t('password')} value={password} onChange={setPassword} describedBy="invitation-requirements" />
      <PasswordRequirements value={password} id="invitation-requirements" />
      <PasswordField id="invitation-confirm" label={t('confirm')} value={confirmPassword} onChange={setConfirmPassword} invalid={confirmPassword.length > 0 && !passwordsMatch} describedBy={confirmPassword.length > 0 && !passwordsMatch ? 'password-mismatch' : undefined} />
      {confirmPassword.length > 0 && !passwordsMatch && <p id="password-mismatch" className="text-sm text-[var(--color-error)]">{t('mismatch')}</p>}
      <button type="submit" disabled={!canSubmit} className="btn-primary flex w-full items-center justify-center gap-2 py-3">{submitting ? <><LoadingSpinner size="sm" />{t('submitting')}</> : <>{t('submit')}<ArrowRight size={17} /></>}</button>
    </form>
    <p>{t('already')} <Link to="/login" className="font-semibold text-[var(--mcq-accent)]">{t('signIn')}</Link></p>
  </AuthFrame>;
}
